// NPC Database for Helix2000
// Quest NPCs, Shopkeepers, and Vendors
// Data sourced from TOML (content/npcs/) — run `npm run compile:content` to regenerate

import type { ProgressionCostQuoteWire } from './types/ProgressionCost';
import { NPC_CONFIG } from './constants/EntityConfig';
import type { QuestGuidance } from './content/generated/quests.gen';
import type { NpcFerryActionId } from './npc/ferryRoutes';

// Runtime Interfaces (Hydrated)
/** UI projection of the server's trainer offers; does not extend any wire message. */
export interface TrainingOption {
    abilityId: string;
    name: string;
    description: string;
    icon: string;
    requiredLevel: number;
    quote: ProgressionCostQuoteWire;
    /** Plan 29 F2: the stat line ("Damage 18–27", …) the client computes from content. Never on the wire. */
    stats?: string[];
}

export interface DialogOption {
    text: string;
    icon?: string;
    action?: string;
    target?: string;
    /** Server-projected ferry action; map/spawn are never dialog authority. */
    ferryAction?: NpcFerryActionId;
    /** Authored speech displayed when this option is chosen in dialogue. */
    speech?: string;
    /** Sub-options displayed after choosing this option. */
    subOptions?: DialogOption[];
}

export interface Quest {
    id: string;
    title: string;
    description: string;
    /** Generated quest/NPC authority for the hand-in destination. */
    endNpcId?: string;
    endNpcName?: string;
    isAccepted: boolean;
    isCompleted: boolean;
    /** Objectives are met, but rewards have not been handed in at the end NPC. */
    isTurnInReady: boolean;
    objectives: Array<{
      type: 'kill' | 'collect' | 'explore' | 'talk' | 'level' | 'equip';
      targetId: string;
      targetName: string;
      description?: string;
      current: number;
      required: number;
    }>;
    requiredLevel: number;
    rewardXP: number;
    rewardGold: number;
    rewardItems: string[];
    /** UI-only canonical reward identity; legacy display strings remain supported. */
    rewardItemDetails?: Array<{ id: string; name: string; quantity: number }>;
    /** The viewing hero's class, when it picks this quest's class_items reward (lane AO). */
    rewardClass?: string;
    prerequisites?: string[];
    /** Featured quest: rendered as a codex page in the NPC dialog (plan 10). */
    featured?: boolean;
    /** Featured quests only: the prerequisite path before this quest, root first, with the player's place on it. */
    path?: Array<{ id: string; state: 'done' | 'active' | 'next' | 'locked' }>;
    /** Compile-time-resolved "where to go" card data (plan 02). */
    guidance?: QuestGuidance;
    /** The ability this quest teaches the viewing player's class (plan 04). `hidden`: the card
     *  shows "?" until turn-in (rewards.ability_hidden). */
    rewardAbility?: { id: string; name: string; hidden?: boolean };
}

export interface QuestOffer {
  id: string;
  title: string;
  description: string;
  objectives: Array<{
    targetName?: string;
    description: string;
    current: number;
    required: number;
  }>;
  rewards: {
    experience: number;
    gold: number;
    items?: Array<{
      id: string;
      name: string;
      quantity: number;
    }>;
  };
  levelRequirement?: number;
  prerequisites?: string[];
}


export interface ShopItem {
    id: string;
    name: string;
    price: number;
    type: string;
    rarity: string;
    stats: Record<string, number>;
    description: string;
    currency?: 'gold' | 'gems';
    stock?: number;
    discount?: number;
}

export interface Service {
    name: string;
    description: string;
    icon: string;
    cost: number;
    action: string;
}

// Database Interface (Static)
export interface NPCData {
  id: string;
  name: string;
  role: import('./npc/npcRoles').NpcRole;
  x: number;
  y: number;
  sprite: string;
  width: number;  // in tiles
  height: number; // in tiles
  color?: string; // Editor visualization color
  dialogue: {
    greeting: string;
    quests: string;
    complete: string;
    goodbye: string;
  };
  availableQuests: string[];
  shopItems?: string[]; // For shop NPCs
}

// Runtime NPC Interface (Extension of Data)
export interface NPC {
    id: string;
    name: string;
    level: number;
    type: string; // Mapped from role
    dialog: {
        greeting: string;
        options: DialogOption[];
    };
    quests: Quest[];
    shopItems: ShopItem[];
    services: Service[];
    trainingOptions?: TrainingOption[];
    /**
     * Client projection (plan 10): featured quests this NPC will offer once
     * their path is walked. Display only, never accepted from here: the server
     * does not send locked quests and keeps every acceptance check.
     */
    featuredTeasers?: Quest[];
    /**
     * Client projection (plan 23 row 44): quests this NPC gave or receives that
     * the reader has finished, most recent first — the "Completed" ribbon.
     */
    completedQuests?: Array<{ id: string; title: string }>;
    /** Client projection (hideout plan P7): the conversation portrait's pose; `talk` when absent. */
    portraitPose?: 'talk' | 'gesture';
}

// NPC_DATABASE derived from generated TOML data (content/npcs/starter_zone.toml)
// Adds default width/height/color that the TOML doesn't carry (rendering concerns)
const NPC_DEFAULTS = { width: 1, height: 2 } as const;
const NPC_COLORS: Record<string, string> = {
  quest_giver: '#FFD700', trainer: '#FF6347', guide: '#87CEEB',
  shop: '#32CD32', guard: '#4682B4', civilian: '#DDA0DD',
};

export const NPC_DATABASE: Record<string, NPCData> = Object.fromEntries(
  Object.entries(NPC_CONFIG).map(([id, npc]) => [
    id,
    {
      ...npc,
      ...NPC_DEFAULTS,
      color: NPC_COLORS[npc.role] || '#FFFFFF',
    } as NPCData,
  ])
);

export function getNPC(npcId: string): NPCData | null {
  return NPC_DATABASE[npcId] || null;
}

export function getAllNPCs(): NPCData[] {
  return Object.values(NPC_DATABASE);
}

export function getQuestNPCs(): NPCData[] {
  return getAllNPCs().filter(npc => npc.role === 'quest_giver' && npc.availableQuests.length > 0);
}

export function getNPCsWithQuestsForPlayer(playerLevel: number, _completedQuests: string[]): NPCData[] {
  const questSystem = require('../../../game/systems/QuestSystem').questSystem;
  
  return getQuestNPCs().filter(npc => {
    const npcQuests = questSystem.getNpcQuests(npc.id, playerLevel);
    const hasAvailableQuests = npcQuests.length > 0;
    
    // Check if has quests AND hasn't completed all of them
    const hasUncompletedQuests = npcQuests.some((quest: any) => !quest.completed);
    
    return hasAvailableQuests && hasUncompletedQuests;
  });
}

// NPC Interaction Responses
export const NPC_INTERACTIONS = {
  questOffer: (npcName: string, questName: string) => 
    `${npcName} wants to give you the quest "${questName}". Do you accept?`,
  
  questComplete: (npcName: string, questName: string) => 
    `Well done! ${npcName} is pleased with your completion of "${questName}".`,
  
  questInProgress: (npcName: string, questName: string, _progress: string) =>
    `${npcName}: "You're making good progress on \"${questName}\"! Keep it up!"`,
  
  allQuestsComplete: (npcName: string) => 
    `${npcName}: "You've completed all my tasks! I'm impressed!"`,
  
  noQuests: (npcName: string) => 
    `${npcName}: "I don't have any tasks for you right now, but check back later!"`
};
